import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Check, AlertCircle, Loader2, PartyPopper, ChevronRight, Rocket } from "lucide-react";
import {
  step1Schema,
  step2Schema,
  type Step1Data,
  type Step2Data,
} from "@/lib/validations/waitlist";
import { submitToHubSpot } from "@/lib/hubspot";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { posthog } from "@/lib/posthog";
import * as Sentry from "@sentry/react";

type FormStep = 1 | 2 | "success";

export const WaitlistForm = () => {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState<1 | 2 | "success">(1);
  const [step1Data, setStep1Data] = useState<Step1Data | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const step1Form = useForm<Step1Data>({
    resolver: zodResolver(step1Schema),
    mode: "onChange",
  });

  const step2Form = useForm<Step2Data>({
    resolver: zodResolver(step2Schema),
    mode: "onChange",
  });

  const onStep1Submit = (data: Step1Data) => {
    setStep1Data(data);
    setCurrentStep(2);

    // GTM event
    if ((window as any).dataLayer) {
      (window as any).dataLayer.push({
        event: "form_step_1_complete",
      });
    }
  };

  const onStep2Submit = async (data: Step2Data) => {
    setIsSubmitting(true);

    try {
      // Combine both steps data
      const fullData = { ...step1Data!, ...data };

      // Add hidden tracking fields
      const submissionData = {
        ...fullData,
        // UTM parameters
        utm_source: new URLSearchParams(window.location.search).get("utm_source") || "",
        utm_medium: new URLSearchParams(window.location.search).get("utm_medium") || "",
        utm_campaign: new URLSearchParams(window.location.search).get("utm_campaign") || "",
        // Additional tracking
        referrer_url: document.referrer,
        landing_page_url: window.location.href,
        signup_timestamp: new Date().toISOString(),
        device_type: /mobile/i.test(navigator.userAgent)
          ? "mobile"
          : /tablet/i.test(navigator.userAgent)
          ? "tablet"
          : "desktop",
        theme_preference: localStorage.getItem("theme") || "system",
        preferred_language: i18n.language,
      };

      if (import.meta.env.DEV) {
        console.log("📝 Submitting form data");
      }

      // GTM event
      if ((window as any).dataLayer) {
        (window as any).dataLayer.push({
          event: "form_step_2_complete",
        });
      }

      // Sentry breadcrumb before submission
      Sentry.addBreadcrumb({
        category: 'waitlist',
        message: 'User started form submission',
        level: 'info',
        data: {
          email_domain: fullData.email.split('@')[1],
          company_size: fullData.companySize,
          has_challenge: !!fullData.challenge,
        },
      });

      // Submit to HubSpot
      await submitToHubSpot(submissionData);

      // GTM success event
      if ((window as any).dataLayer) {
        (window as any).dataLayer.push({
          event: "form_submit_success",
        });
      }

      // PostHog user identification & conversion event
      if (posthog.__loaded) {
        posthog.identify(fullData.email, {
          firstName: fullData.firstName,
          lastName: fullData.lastName,
          company: fullData.company,
          jobTitle: fullData.jobTitle,
          companySize: fullData.companySize,
        });
        
        posthog.capture('waitlist_signup_completed', {
          company_size: fullData.companySize,
          has_challenge: !!fullData.challenge,
        });
      }

      setCurrentStep("success");
    } catch (error) {
      console.error("❌ Form submission error:", error);

      // Sentry error tracking
      Sentry.captureException(error, {
        tags: {
          section: 'waitlist_form',
          action: 'submit',
        },
        contexts: {
          form_data: {
            email_domain: step1Data?.email.split('@')[1],
            company: step2Form.getValues('company') || 'not_provided',
            company_size: step2Form.getValues('companySize') || 'not_provided',
            language: i18n.language,
          },
        },
      });

      // GTM error event
      if ((window as any).dataLayer) {
        (window as any).dataLayer.push({
          event: "form_submit_error",
          error_message: error instanceof Error ? error.message : "Unknown error",
        });
      }

      toast({
        title: t("form.errors.genericError"),
        description: t("form.errors.tryAgain"),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const companySizes = ["1-10", "11-50", "51-200", "201-500", "501+"];

  const isStep1Complete = currentStep === 2 || currentStep === "success";
  const isStep2Complete = currentStep === "success";

  if (currentStep === "success") {
    return (
      <div className="text-center py-12 animate-fade-in">
        <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-brand-green to-brand-teal mb-6 animate-scale-in">
          <PartyPopper className="w-12 h-12 text-white" strokeWidth={2} />
        </div>
        <h3 className="font-clash text-2xl md:text-3xl font-bold text-foreground mb-4 tracking-tight">
          {t("form.success.title")}
        </h3>
        <p className="text-muted-foreground max-w-md mx-auto">
          {t("form.success.subtitle")}
        </p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-2xl mx-auto">
      {/* Progress Indicator */}
      <div className="mb-8">
        <div className="flex items-center justify-center gap-2 mb-4">
          <div
            className={cn(
              "w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors",
              "bg-brand-teal text-white"
            )}
          >
            {isStep1Complete ? <Check className="w-5 h-5" /> : "1"}
          </div>
          <div className={cn("h-1 w-16 transition-colors", isStep1Complete ? "bg-brand-teal" : "bg-muted")} />
          <div
            className={cn(
              "w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors",
              isStep2Complete || currentStep === 2
                ? "bg-brand-teal text-white"
                : "bg-muted text-muted-foreground"
            )}
          >
            2
          </div>
        </div>
        <p className="text-center text-sm text-muted-foreground">
          {typeof currentStep === "number" && t("form.progress", { current: currentStep })}
        </p>
      </div>

      {/* Step 1: Basic Info */}
      {currentStep === 1 && (
        <form onSubmit={step1Form.handleSubmit(onStep1Submit)} className="space-y-6 animate-fade-in">
          {/* LinkedIn OAuth - Hidden until integration complete */}
          <div className="hidden">
            <div className="text-center">
              <Button
                type="button"
                disabled
                className="w-full bg-[#0077B5] hover:bg-[#0077B5]/90 text-white opacity-50 cursor-not-allowed"
                size="lg"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
                {t("form.step1.linkedinCta")} <span className="ml-2 text-xs">(Coming soon)</span>
              </Button>
              <p className="text-sm text-muted-foreground mt-4">{t("form.step1.or")}</p>
            </div>
          </div>

          <div className="space-y-4">
            {/* First Name */}
            <div>
              <Label htmlFor="firstName" className="text-foreground">
                {t("form.step1.firstName")} *
              </Label>
              <div className="relative">
                <Input
                  id="firstName"
                  {...step1Form.register("firstName")}
                  className={cn(
                    "mt-1",
                    step1Form.formState.errors.firstName && "border-brand-red",
                    !step1Form.formState.errors.firstName && step1Form.watch("firstName") && "border-brand-green"
                  )}
                  onFocus={() => {
                    if ((window as any).dataLayer && !step1Form.formState.isSubmitted) {
                      (window as any).dataLayer.push({ event: "form_start" });
                    }
                  }}
                />
                {!step1Form.formState.errors.firstName && step1Form.watch("firstName") && (
                  <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-brand-green" />
                )}
              </div>
              {step1Form.formState.errors.firstName && (
                <p className="text-sm text-brand-red mt-1 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {t(step1Form.formState.errors.firstName.message as string)}
                </p>
              )}
              <p className="text-xs text-muted-foreground mt-1.5">
                {t("form.microcopy.firstName")}
              </p>
            </div>

            {/* Last Name */}
            <div>
              <Label htmlFor="lastName" className="text-foreground">
                {t("form.step1.lastName")} *
              </Label>
              <div className="relative">
                <Input
                  id="lastName"
                  {...step1Form.register("lastName")}
                  className={cn(
                    "mt-1",
                    step1Form.formState.errors.lastName && "border-brand-red",
                    !step1Form.formState.errors.lastName && step1Form.watch("lastName") && "border-brand-green"
                  )}
                />
                {!step1Form.formState.errors.lastName && step1Form.watch("lastName") && (
                  <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-brand-green" />
                )}
              </div>
              {step1Form.formState.errors.lastName && (
                <p className="text-sm text-brand-red mt-1 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {t(step1Form.formState.errors.lastName.message as string)}
                </p>
              )}
              <p className="text-xs text-muted-foreground mt-1.5">
                {t("form.microcopy.lastName")}
              </p>
            </div>

            {/* Email */}
            <div>
              <Label htmlFor="email" className="text-foreground">
                {t("form.step1.email")} *
              </Label>
              <div className="relative">
                <Input
                  id="email"
                  type="email"
                  {...step1Form.register("email")}
                  className={cn(
                    "mt-1",
                    step1Form.formState.errors.email && "border-brand-red",
                    !step1Form.formState.errors.email && step1Form.watch("email") && "border-brand-green"
                  )}
                />
                {!step1Form.formState.errors.email && step1Form.watch("email") && (
                  <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-brand-green" />
                )}
              </div>
              {step1Form.formState.errors.email && (
                <p className="text-sm text-brand-red mt-1 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {t(step1Form.formState.errors.email.message as string)}
                </p>
              )}
              <p className="text-xs text-muted-foreground mt-1.5">
                {t("form.microcopy.email")}
              </p>
            </div>
          </div>

          <Button
            type="submit"
            size="lg"
            className="w-full bg-brand-orange hover:bg-brand-orange/90 text-white font-semibold group"
          >
            {t("form.step1.cta")}
            <ChevronRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
          </Button>
        </form>
      )}

      {/* Step 2: Additional Details */}
      {currentStep === 2 && (
        <form onSubmit={step2Form.handleSubmit(onStep2Submit)} className="space-y-6 animate-fade-in">
          <div className="space-y-4">
            {/* Company Name */}
            <div>
              <Label htmlFor="company" className="text-foreground">
                {t("form.step2.company")} *
              </Label>
              <div className="relative">
                <Input
                  id="company"
                  {...step2Form.register("company")}
                  className={cn(
                    "mt-1",
                    step2Form.formState.errors.company && "border-brand-red",
                    !step2Form.formState.errors.company && step2Form.watch("company") && "border-brand-green"
                  )}
                />
                {!step2Form.formState.errors.company && step2Form.watch("company") && (
                  <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-brand-green" />
                )}
              </div>
              {step2Form.formState.errors.company && (
                <p className="text-sm text-brand-red mt-1 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {t(step2Form.formState.errors.company.message as string)}
                </p>
              )}
              <p className="text-xs text-muted-foreground mt-1.5">
                {t("form.microcopy.company")}
              </p>
            </div>

            {/* Job Title */}
            <div>
              <Label htmlFor="jobTitle" className="text-foreground">
                {t("form.step2.jobTitle")} *
              </Label>
              <div className="relative">
                <Input
                  id="jobTitle"
                  {...step2Form.register("jobTitle")}
                  className={cn(
                    "mt-1",
                    step2Form.formState.errors.jobTitle && "border-brand-red",
                    !step2Form.formState.errors.jobTitle && step2Form.watch("jobTitle") && "border-brand-green"
                  )}
                />
                {!step2Form.formState.errors.jobTitle && step2Form.watch("jobTitle") && (
                  <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-brand-green" />
                )}
              </div>
              {step2Form.formState.errors.jobTitle && (
                <p className="text-sm text-brand-red mt-1 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {t(step2Form.formState.errors.jobTitle.message as string)}
                </p>
              )}
              <p className="text-xs text-muted-foreground mt-1.5">
                {t("form.microcopy.jobTitle")}
              </p>
            </div>

            {/* Company Size */}
            <div>
              <Label htmlFor="companySize" className="text-foreground">
                {t("form.step2.companySize")} *
              </Label>
              <Select onValueChange={(value) => step2Form.setValue("companySize", value, { shouldValidate: true })}>
                <SelectTrigger
                  className={cn(
                    "mt-1",
                    step2Form.formState.errors.companySize && "border-brand-red",
                    !step2Form.formState.errors.companySize && step2Form.watch("companySize") && "border-brand-green"
                  )}
                >
                  <SelectValue placeholder={t("form.step2.companySize")} />
                </SelectTrigger>
                <SelectContent className="bg-popover z-50">
                  {companySizes.map((size) => (
                    <SelectItem key={size} value={size}>
                      {t(`form.step2.companySizes.${size}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {step2Form.formState.errors.companySize && (
                <p className="text-sm text-brand-red mt-1 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {t(step2Form.formState.errors.companySize.message as string)}
                </p>
              )}
              <p className="text-xs text-muted-foreground mt-1.5">
                {t("form.microcopy.companySize")}
              </p>
            </div>

            {/* Challenge (Optional) */}
            <div>
              <Label htmlFor="challenge" className="text-foreground">
                {t("form.step2.challenge")}
              </Label>
              <Textarea
                id="challenge"
                {...step2Form.register("challenge")}
                placeholder={t("form.step2.challengePlaceholder")}
                className="mt-1 min-h-[100px]"
                maxLength={500}
              />
              <div className="flex items-center justify-between mt-1.5">
                <p className="text-xs text-muted-foreground">
                  {t("form.microcopy.challenge")}
                </p>
                <p className="text-xs text-muted-foreground">
                  {step2Form.watch("challenge")?.length || 0}/500
                </p>
              </div>
            </div>
          </div>

          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              size="lg"
              onClick={() => setCurrentStep(1)}
              className="flex-1"
              disabled={isSubmitting}
            >
              Back
            </Button>
            <Button
              type="submit"
              size="lg"
              className="flex-1 bg-brand-teal hover:bg-brand-teal/90 text-white font-bold group"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Rocket className="mr-2 w-5 h-5 transition-transform group-hover:-translate-y-1" />
                  {t("form.step2.cta")}
                </>
              )}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};
